// ─────────────────────────────────────────────────────────────────
// Glass-i — Reusable inline sub-grid for an employee's 1:N records
// (documents / contacts / addresses). Stage HRMS-2.
//
// Compact inline CRUD: add a draft row, edit cells, Save persists that
// row (create or update) via the injected api, Delete removes it. Only
// usable for an existing employee (needs an id).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputNumber, Select, Switch, DatePicker, Table, Space, Tooltip, Popconfirm, message } from 'antd';
import { PlusOutlined, SaveOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

let _k = 0;
const nextKey = () => { _k += 1; return `sub-${_k}`; };

// columns: [{ key, title, type: 'text'|'number'|'select'|'switch'|'date', options?, width?, placeholder? }]
export default function EmployeeSubGrid({ employeeId, api, columns, makeBlank, emptyText }) {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId) { setRows([]); return; }
    setLoading(true);
    try {
      const out = await api.list(employeeId);
      setRows(((out && out.rows) || []).map((r) => ({ ...r, _key: r.id, _dirty: false, _draft: false })));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load.');
    } finally { setLoading(false); }
  }, [employeeId, api]);
  useEffect(() => { load(); }, [load]);

  const patch = (key, p) => setRows((prev) => prev.map((r) => r._key === key ? { ...r, ...p, _dirty: true } : r));

  const addRow = () => setRows((prev) => [...prev, { _key: nextKey(), _draft: true, _dirty: true, ...makeBlank() }]);

  const buildPayload = (row) => {
    const out = {};
    for (const c of columns) {
      let v = row[c.key];
      if (c.type === 'date') v = v && v.format ? v.format('YYYY-MM-DD') : (v || null);
      out[c.key] = v === undefined ? null : v;
    }
    return out;
  };

  const saveRow = async (row) => {
    setBusyKey(row._key);
    try {
      const payload = buildPayload(row);
      if (row._draft) await api.create(employeeId, payload);
      else await api.update(employeeId, row.id, payload);
      message.success('Saved.');
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save.');
    } finally { setBusyKey(null); }
  };

  const removeRow = async (row) => {
    if (row._draft) { setRows((prev) => prev.filter((r) => r._key !== row._key)); return; }
    setBusyKey(row._key);
    try { await api.remove(employeeId, row.id); message.success('Removed.'); load(); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to remove.'); }
    finally { setBusyKey(null); }
  };

  const renderCell = (c) => (v, row) => {
    const val = row[c.key];
    if (c.type === 'select') {
      return <Select size="small" variant="borderless" style={{ width: '100%' }} value={val || undefined}
        placeholder={c.placeholder || ''} options={c.options} allowClear={c.allowClear}
        showSearch optionFilterProp="label" onChange={(x) => patch(row._key, { [c.key]: x })} />;
    }
    if (c.type === 'switch') {
      return <Switch size="small" checked={val === true} onChange={(x) => patch(row._key, { [c.key]: x })} />;
    }
    if (c.type === 'number') {
      return <InputNumber size="small" variant="borderless" controls={false} style={{ width: '100%' }}
        value={val} onChange={(x) => patch(row._key, { [c.key]: x })} />;
    }
    if (c.type === 'date') {
      const dv = val ? (val.format ? val : dayjs(val)) : null;
      return <DatePicker size="small" variant="borderless" style={{ width: '100%' }} format="DD-MM-YYYY"
        value={dv && dv.isValid && dv.isValid() ? dv : null}
        onChange={(x) => patch(row._key, { [c.key]: x })} />;
    }
    return <Input size="small" variant="borderless" value={val || ''} placeholder={c.placeholder || ''}
      maxLength={c.maxLength || 200} onChange={(e) => patch(row._key, { [c.key]: e.target.value })} />;
  };

  const tableColumns = [
    ...columns.map((c) => ({ title: c.title, dataIndex: c.key, width: c.width, render: renderCell(c) })),
    { title: '', key: '_a', width: 74, align: 'center', fixed: 'right',
      render: (_v, row) => (
        <Space size={2}>
          <Tooltip title="Save row"><Button size="small" type="text" icon={<SaveOutlined />}
            loading={busyKey === row._key} disabled={!row._dirty} onClick={() => saveRow(row)} /></Tooltip>
          <Popconfirm title="Remove this row?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => removeRow(row)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) }
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>Add</Button>
        <Tooltip title="Reload"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
      </div>
      <Table className="hr-grid" size="small" rowKey="_key" loading={loading}
        dataSource={rows} columns={tableColumns} pagination={false}
        scroll={{ x: 'max-content' }} rowClassName={(r) => r._dirty ? 'hr-dirty-row' : ''}
        locale={{ emptyText: emptyText || 'No records yet. Click Add to start.' }} />
    </div>
  );
}
